/**
 * Who is signed in — a **server** question, so TanStack Query owns it, not
 * Zustand.
 *
 * The rule this follows (see stores/ui-store.ts for the other half): anything
 * the backend is the authority on lives in the query cache. Copying the user
 * into a Zustand store would create a second copy that goes stale the moment the
 * session expires in another tab, and then two places to fix it.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { useApiQuery } from "@/lib/query";
import type { CurrentUser, Me } from "./types";

/** Role and cost cap from `GET /auth/me`. Separate from the session query above: that
 *  one is the auth block's answer to "who is signed in", this one is the
 *  workbench's answer to "what may they spend". */
export function useMe() {
  return useApiQuery<Me>("/auth/me", {}, { staleTime: 5 * 60_000 });
}

/** Where to return to after signing in. The `auth` block always sends the
 *  browser to one configured URL, so the intended destination is remembered on
 *  this side of the round trip instead of round-tripping through the provider. */
const RETURN_TO = "workbench:return-to";

/** True only for the one status that means "nobody is signed in". */
function isUnauthorized(error: unknown): boolean {
  return (error as ApiError)?.status === 401;
}

export function useCurrentUser() {
  return useApiQuery<CurrentUser>(
    "/auth/me",
    {},
    {
      // A 401 is the *answer* ("nobody is signed in"), not a failure to retry —
      // retrying it would sit the login screen behind a couple of blank seconds.
      // Anything else is a real failure and worth one more attempt: the backend
      // restarting mid-request must not read as a logout.
      retry: (attempt, error) => !isUnauthorized(error) && attempt < 2,
      staleTime: 5 * 60_000,
    },
  );
}

/** True once we know the answer either way, so routes never flash the login
 *  screen at someone who is in fact signed in. */
export function useAuthStatus() {
  const query = useCurrentUser();
  const signedOut = query.isError && isUnauthorized(query.error);
  return {
    user: query.data ?? null,
    isResolved: query.isSuccess || query.isError,
    isAuthenticated: query.isSuccess,
    /** Signed out for certain — a 401, not a guess. */
    signedOut,
    /** The API could not be reached or answered with something unexpected.
     *  Distinct from signedOut on purpose: sending someone to the login screen
     *  because the backend hiccuped makes them re-authenticate to fix a problem
     *  that was never theirs, and loses the URL they asked for. */
    unreachable: query.isError && !signedOut,
    error: query.error,
  };
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    // The block answers logout with a 303 to the frontend. We do not want the
    // browser to follow it here — the SPA decides where to go next.
    mutationFn: () => api.postExpectingRedirect("/auth/logout"),
    onSuccess: () => {
      // Clear everything, not just the user: the note list, the run list and
      // anything else in the cache was fetched as *that* person. The hard
      // navigation also drops any component state still holding their data.
      queryClient.clear();
      window.location.href = "/login";
    },
  });
}

/** Start the OAuth round trip. A full navigation, not fetch: the browser has to
 *  visit Microsoft, and XHR cannot follow a cross-origin login redirect. */
export function signIn(next: string = "/") {
  sessionStorage.setItem(RETURN_TO, next);
  window.location.href = "/api/v1/auth/login";
}

/** Consume the remembered destination. Reading clears it, so a later refresh of
 *  the landing page does not bounce the user somewhere unexpected. */
export function takeReturnTo(): string {
  const next = sessionStorage.getItem(RETURN_TO);
  sessionStorage.removeItem(RETURN_TO);
  // Only in-app paths: an absolute URL here would turn our own sign-in into an
  // open redirect, which is a phishing primitive.
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
}
